import { useEffect, useRef, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'
import { LocationProvider } from '@reach/router'
import { Helmet } from 'react-helmet'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'

import Router from 'src/components/router/Router'
import ErrorBoundary from 'src/components/shared/ErrorBoundary'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { AlertContextProvider } from 'src/contexts/AlertContext'
import 'src/styles/global.css'
import { EscritoContextProvider } from 'src/contexts/EscritoContext/EscritoContext'
import { LexnetStatusContextProvider } from 'src/contexts/LexnetStatusContext'
import { SnackbarContextProvider } from 'src/contexts/SnackbarContext'
import { SpinnerContextProvider } from 'src/contexts/SpinnerContext'
import { TipoMensajeContextProvider } from 'src/contexts/TipoMensajeContext'
import useCookie from 'src/hooks/useCookie'
import useQueryToken from 'src/hooks/useQueryToken'
import ThemeProvider from 'src/providers/ThemeProvider'
import * as AuthenticationService from 'src/services/AuthenticationService'
import { setAppTokens, setUserAction } from 'src/state/authentication/actions'
import store from 'src/state/store'
import CookiebotPlugin from './plugins/cookiebot'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  }
})

function AppWrapper() {
  const appTokens = useQueryToken()
  const [loading, setLoading] = useState(true)
  const { removeLexnetCookie } = useCookie()

  useEffect(() => {
    setLoading(true)
    store.dispatch(setAppTokens(appTokens))
    AuthenticationService.getUser()
      .then(user => {
        store.dispatch(setUserAction(user))
      })
      .catch(() => removeLexnetCookie())
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line
  }, [appTokens])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider>
        <SnackbarContextProvider>
          <SpinnerContextProvider>
            <AlertContextProvider>
              <LocationProvider>
                <LexnetStatusContextProvider>
                  <TipoMensajeContextProvider>
                    <EscritoContextProvider>
                      <Helmet defaultTitle="LexNET" />
                      {loading ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 32
                          }}
                        >
                          <Loader fullscreen size="large" />
                        </div>
                      ) : (
                        <Router />
                      )}
                    </EscritoContextProvider>
                  </TipoMensajeContextProvider>
                </LexnetStatusContextProvider>
              </LocationProvider>
            </AlertContextProvider>
          </SpinnerContextProvider>
        </SnackbarContextProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
export default function App(): JSX.Element {
  const cookiebot = useRef(new CookiebotPlugin())

  useEffect(() => {
    cookiebot.current.init()
  }, [])

  return (
    <Box>
      <Grid>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <AppWrapper />
            </Provider>
          </QueryClientProvider>
        </ErrorBoundary>
      </Grid>
    </Box>
  )
}
